
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PhoneInput } from "@/components/ui/phone-input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { countries } from "@/lib/countries";

const translations = {
  en: {
    title: "Book Free Consultation",
    name: "Full Name",
    email: "Email Address",
    phone: "Phone Number",
    country: "Country of Residence",
    destination: "Preferred Study Destination",
    message: "Your Message",
    submit: "Book Consultation",
    success: "Thank you! We'll contact you soon.",
    error: "Error submitting form. Please try again."
  }
};

export function ConsultationForm({ language = "en", open, onOpenChange }) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    destination: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to Supabase
      const { error } = await supabase
        .from('consultations')
        .insert([{
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          preferred_destination: formData.destination,
          message: formData.message
        }]);

      if (error) throw error;

      // Send email notification
      await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_RESEND_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: 'The Horn Educational Network <info@thehorneducation.com>',
          to: 'info@thehorneducation.com',
          subject: '📞 New Consultation Request',
          html: `
            <h2>New Consultation Request</h2>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Phone:</strong> ${formData.phone}</p>
            <p><strong>Country:</strong> ${formData.country}</p>
            <p><strong>Preferred Destination:</strong> ${formData.destination}</p>
            <p><strong>Message:</strong> ${formData.message}</p>
          `
        })
      });

      toast({
        title: translations[language].success,
        duration: 3000
      });

      onOpenChange(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        country: "",
        destination: "",
        message: ""
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: translations[language].error,
        variant: "destructive",
        duration: 3000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{translations[language].title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].name}
              </label>
              <input
                required
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].email}
              </label>
              <input
                required
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].phone}
              </label>
              <PhoneInput
                value={formData.phone}
                onChange={(value) => setFormData({ ...formData, phone: value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].country}
              </label>
              <select
                required
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="">Select country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].destination}
              </label>
              <select
                required
                value={formData.destination}
                onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                className="w-full p-2 rounded-md border"
              >
                <option value="">Select destination</option>
                <option value="UK">United Kingdom</option>
                <option value="Malaysia">Malaysia</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                {translations[language].message}
              </label>
              <textarea
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full p-2 rounded-md border"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {translations[language].submit}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
